import React, { ReactNode } from 'react';

import { useAskAiInfiniteRules } from '@lumapps/ask-ai-rules/hooks/useAskAiInfiniteRules';
import { AskAiRule, ListRulesParamsDTO } from '@lumapps/ask-ai-rules/types';
import { SelectButton } from '@lumapps/combobox/components/SelectButton';
import { SingleSelectTextFieldProps } from '@lumapps/combobox/components/SelectTextField';
import { mdiMenuDown } from '@lumapps/lumx/icons';
import { Chip, Emphasis, Icon } from '@lumapps/lumx/react';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

export type AiRulesPickerFieldProps = Omit<
    SingleSelectTextFieldProps<AskAiRule>,
    'getOptionId' | 'getOptionName' | 'autoFilter' | 'selectionType' | 'onOpen' | 'options' | 'status'
> & {
    /** options to pass in to the backend query */
    getRulesParams?: ListRulesParamsDTO;
    /** Before input */
    before?: ReactNode;
    /** Popover className */
    popoverClassName?: string;
};

/**
 * @param AiRulesPickerFieldProps
 * @family Pickers
 * @returns AiRulesPickerField
 */
export const AiRulesPickerField: React.FC<AiRulesPickerFieldProps> = ({
    getRulesParams = {},
    onChange,
    before,
    popoverClassName,
    ...selectTextFieldProps
}) => {
    const [isOpen, setOpen] = React.useState<boolean>(false);

    const {
        items: rules,
        isSuccess,
        fetchNextPage,
        hasNextPage,
        baseLoadingStatus,
    } = useAskAiInfiniteRules(getRulesParams, {
        enabled: isOpen,
    });

    return (
        <SelectButton<AskAiRule>
            {...selectTextFieldProps}
            emphasis={Emphasis.medium}
            size="s"
            label={selectTextFieldProps.value?.title || ''}
            options={rules}
            getOptionId="id"
            getOptionName="title"
            value={selectTextFieldProps.value}
            onOpen={() => setOpen(true)}
            onChange={onChange}
            popoverProps={{ className: popoverClassName }}
            as={Chip}
            before={before}
            after={<Icon icon={mdiMenuDown} />}
            onLoadMore={isSuccess && hasNextPage ? fetchNextPage : () => {}}
            isLoading={baseLoadingStatus === BaseLoadingStatus.loading}
            status={baseLoadingStatus}
        />
    );
};
